/**
 * PATH: packages/email-hub/src/feature/email-layout/DeepSyncControl.tsx
 * FILE_ID: EMAIL-LAYOUT-DEEP-SYNC-001
 * PURPOSE: Render the existing split sync button and portalled window menu.
 */

import { Activity, ChevronDown, Loader2, RefreshCw } from 'lucide-react'
import { createPortal } from 'react-dom'
import type { EmailLayoutController } from './useEmailLayoutController'

export function DeepSyncControl({
  layout,
}: {
  layout: EmailLayoutController
}) {
  return (
    <div className="relative flex" ref={layout.deepSyncRef}>
      <button
        onClick={() => { void layout.handleSync() }}
        disabled={layout.isSyncing}
        className="flex-1 text-xs px-2 py-1.5 rounded-l-md border border-border hover:bg-accent transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
        title="Sync the last 30 days"
      >
        {layout.isSyncing
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <RefreshCw className="w-3 h-3" />}
        {layout.isSyncing ? 'Syncing...' : 'Sync Now'}
      </button>
      <button
        type="button"
        onClick={() => layout.setDeepSyncOpen(value => !value)}
        disabled={layout.isSyncing}
        className="px-1 py-1.5 rounded-r-md border border-l-0 border-border hover:bg-accent transition-colors disabled:opacity-50"
        aria-haspopup="menu"
        aria-expanded={layout.deepSyncOpen}
        aria-label="Sync window options"
        title="Sync window options"
      >
        <ChevronDown className="w-3 h-3" />
      </button>
      {layout.deepSyncOpen && layout.deepSyncCoords && createPortal(
        <div
          ref={layout.deepSyncMenuRef}
          role="menu"
          className="fixed z-[1000] max-h-[60vh] overflow-y-auto bg-popover border border-border rounded-md shadow-lg py-1 text-xs"
          style={{
            left: layout.deepSyncCoords.left,
            width: layout.deepSyncCoords.width,
            bottom: layout.deepSyncCoords.bottom,
          }}
        >
          <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            Sync window
          </p>
          {[7, 30, 90, 180].map(days => (
            <button
              key={days}
              type="button"
              role="menuitem"
              onClick={() => {
                layout.setDeepSyncOpen(false)
                void layout.handleSync({ daysBack: days })
              }}
              className="w-full text-left px-2 py-1.5 hover:bg-accent flex items-center justify-between"
            >
              <span>Last {days} days</span>
              {days === 30 && (
                <span className="text-[9px] text-muted-foreground uppercase">
                  default
                </span>
              )}
              {days === 180 && (
                <span className="text-[9px] text-amber-500 uppercase">
                  deep
                </span>
              )}
            </button>
          ))}
          <div className="border-t border-border my-1" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              layout.setDeepSyncOpen(false)
              layout.openSettings('email-sync')
            }}
            className="w-full text-left px-2 py-1.5 hover:bg-accent flex items-center justify-between"
            title="Live sync status, backfill progress, and live mailbox diagnostics"
          >
            <span className="flex items-center gap-1.5">
              <Activity className="w-3 h-3" /> Open sync settings
            </span>
            <span className="text-[9px] uppercase opacity-70">
              {layout.backfill.progressPct}%
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              layout.setDeepSyncOpen(false)
              void layout.handleSync({ daysBack: 90, forceFull: true })
            }}
            className="w-full text-left px-2 py-1.5 hover:bg-accent flex items-center justify-between text-amber-600 dark:text-amber-400"
            title="Bypass Microsoft Graph delta cursor and pull a fresh 90-day window - use when the Inbox stops at an old date"
          >
            <span>Force full resync</span>
            <span className="text-[9px] uppercase opacity-70">
              bypass delta
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              layout.setDeepSyncOpen(false)
              layout.backfill.reset()
            }}
            className="w-full text-left px-2 py-1.5 hover:bg-accent flex items-center justify-between text-amber-500"
            title="Reset the historical backfill cursor and re-walk from now backwards"
          >
            <span>Restart backfill</span>
            <span className="text-[9px] uppercase opacity-70">
              all history
            </span>
          </button>
        </div>,
        document.body,
      )}
    </div>
  )
}
